import { useEffect, useMemo, useState } from 'react'
import { Box, Skeleton, useTheme } from '@mui/material'

import classes from './index.styles'

const LineChartSkeleton = () => {
  const theme = useTheme()
  const [time, setTime] = useState(0)
  const trendPoints = useMemo(() => {
    const xPositions = [2, 16, 30, 46, 60, 74, 88, 98]
    const minY = 30
    const maxY = 42

    return xPositions.map((x) => ({
      x,
      y: Math.round(minY + Math.random() * (maxY - minY)),
    }))
  }, [])

  useEffect(() => {
    let frameId = 0
    const start = performance.now()
    let lastUpdate = 0
    const targetFrameMs = 1000 / 30

    const tick = (now: number) => {
      if (now - lastUpdate >= targetFrameMs) {
        setTime((now - start) / 1000)
        lastUpdate = now
      }
      frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [])

  const pointsString = trendPoints.map((point) => `${point.x},${point.y}`).join(' ')
  const shimmerOffset = -((time * 70) % 248)
  const lineOpacity = 0.76 + Math.sin((time * Math.PI * 2) / 1.8) * 0.12

  return (
    <Box data-testid="line-chart-skeleton" sx={classes.skeletonRoot}>
      <Box sx={classes.skeletonContainer}>
        <Box sx={classes.skeletonYAxisLabels}>
          {[0, 1, 2, 3, 4].map((label) => (
            <Skeleton key={label} variant="text" width={28} height={16} />
          ))}
        </Box>

        <Box sx={classes.skeletonPlotFrame}>
          {[0, 1, 2, 3].map((row) => (
            <Skeleton key={row} variant="rectangular" animation={false} sx={classes.skeletonGridLine(row)} />
          ))}

          <Box sx={classes.skeletonSvgWrapper}>
            <Box component="svg" viewBox="0 0 100 60" preserveAspectRatio="xMidYMid slice" sx={classes.skeletonSvg}>
              <polyline
                points={pointsString}
                fill="none"
                stroke="#00000025"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                opacity={lineOpacity}
              />

              <polyline
                points={pointsString}
                fill="none"
                stroke="#00000025"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="28 220"
                strokeDashoffset={shimmerOffset}
                vectorEffect="non-scaling-stroke"
                opacity={0.95}
              />

              {trendPoints.map((point, index) => (
                <circle
                  key={`point-${point.x}-${point.y}`}
                  cx={point.x}
                  cy={point.y}
                  r={1.05 + Math.sin((time * Math.PI * 2) / 1.3 - index * 0.35) * 0.12}
                  fill="#00000025"
                  stroke={theme.palette.background.paper}
                  strokeWidth="0.35"
                  opacity={0.78 + Math.sin((time * Math.PI * 2) / 1.4 - index * 0.35) * 0.18}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={classes.skeletonMiddleBarContainer}>
          <Skeleton variant="rectangular" width="100%" height={60} sx={classes.skeletonMiddleBar} />
        </Box>

        <Box sx={classes.skeletonBottomLabels}>
          <Skeleton variant="text" width={75} height={16} />
        </Box>
      </Box>
    </Box>
  )
}

export default LineChartSkeleton
